import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Upload } from 'lucide-react';
import { type HTMLProps, useRef, useState, type DragEvent } from 'react';
import { Icon } from 'semantic-ui-react';

import s from './file-uploader.scss';

export interface FileUploaderProps extends HTMLProps<HTMLDivElement> {
  selectedFile?: File;
  onSelectFile: (file?: File) => void;
  className?: string;
}

const FileUploader = ({
  selectedFile,
  onSelectFile,
  className,
  ...fieldProps
}: FileUploaderProps) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const selectFile = (f?: File) => {
    setDragActive(false);
    onSelectFile(f);
    // Clear the input value when file is removed to allow re-selection of the same file
    if (!f && inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div
      className={classNames(
        s.fileUploadButton,
        className,
        dragActive ? s.dragActive : '',
      )}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={e => {
        e.preventDefault();
        e.stopPropagation();
        selectFile(e.dataTransfer?.files?.[0]);
      }}
      {...fieldProps}
    >
      <div className={s.container}>
        <input
          ref={inputRef}
          aria-label="Upload file"
          id="input-file-upload"
          multiple={false}
          type="file"
          onChange={e => {
            e.preventDefault();
            selectFile(e.target?.files?.[0]);
          }}
        />
        <Upload className={s.uploadIcon} />

        {selectedFile ? (
          <div className={s.selectedFile}>
            <button
              aria-label="Remove file"
              className={s.removeButton}
              type="button"
              onClick={() => selectFile(undefined)}
            >
              <Icon name="times circle" />
            </button>
            {selectedFile.name}
          </div>
        ) : (
          <label htmlFor="input-file-upload">
            Drag and drop your file here or
            <button
              type="button"
              onClick={() => inputRef?.current?.click()}
              className={s.uploadButton}
            >
              click to select a file
            </button>
          </label>
        )}
      </div>
    </div>
  );
};

export default withStyles(s)(FileUploader);
